import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    Grid,
    Card,
    CardContent,
    CardHeader,
    MenuItem,
    FormControlLabel,
    FormGroup,
    Switch,
    Checkbox,
    Button,
    CircularProgress,
    Alert,
    Divider,
    InputAdornment,
    Paper,
    Stack,
    Container,
    Breadcrumbs,
    Link,
    useTheme,
    alpha,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete,
    Chip
} from '@mui/material';
import {
    Person,
    Phone,
    Email,
    Business,
    Work,
    Group,
    CloudUpload,
    Refresh as RefreshIcon,
    NavigateNext,
    Home,
    People,
    School,
    Badge,
    CheckBoxOutlineBlank,
    CheckBox as CheckBoxIcon
} from '@mui/icons-material';
import DescriptionIcon from '@mui/icons-material/Description';
import { getEntities } from '../../../../api/entityApi';
import { getDepartments } from '../../../../api/departmentApi';
import { getLocations } from '../../../../api/locationApi';
import { useGetRolesQuery } from '../../../../services/roleApi';
import { createEmployee, getEmployees, bulkUploadEmployees } from '../../../../api/employeeApi';
import { getDesignations } from '../../../../api/designationApi';
import { useErrorModal } from '../../../../hooks/useErrorModal';
import AlertDialog from '../../../../components/common/AlertDialog';
import useAlertDialog from '../../../../hooks/useAlertDialog';
import BulkUploadModal from '../../../../components/common/BulkUploadModal';
import { getOnboardingTemplatesByEntity } from '../../../../api/onboardingApi';


const EDUCATION_LEVELS = [
    { value: 'level1', label: 'Level 1' },
    { value: 'level2', label: 'Level 2' },
];

const EMPLOYMENT_TYPES = [
    { value: 'Full-time', label: 'Full-time' },
    { value: 'Part-time', label: 'Part-time' },
    { value: 'Contract', label: 'Contract' },
    { value: 'Intern', label: 'Intern' },
];

// Validation functions
const validateEmail = (email) => {
    if (!email) return false;
    const trimmedEmail = String(email).trim();
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(trimmedEmail?.toLowerCase());
};

const validateMobile = (mobile) => {
    return /^[0-9]{10}$/.test(mobile);
};

const formInputStyle = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        transition: 'all 0.2s ease-in-out',
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#7522D1',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#7522D1',
            borderWidth: '2px',
        },
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: '#7522D1',
    },
};

const InitiateOnboarding = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const { showError, ErrorModal } = useErrorModal();
    const { alertDialog, showAlert, closeAlert } = useAlertDialog();

    // Form state
    const [formData, setFormData] = useState({
        employeeId: '',
        firstName: '',
        lastName: '',
        mobileNumber: '',
        email: '',
        jobLocation: '',
        entityId: '',
        departmentId: '',
        roleId: '',
        designationId: '',
        educationLevel: '',
        employmentType: '',
        isFresher: '',
        hasExistingPF: false,
        collect12BB: false,
        collectESIC: false,
        collectDocuments: true,
        collectJoiningDocuments: false,
        managerId: ''
    });

    // Track whether form has been submitted (to show all errors)
    const [submitted, setSubmitted] = useState(false);

    // Form errors
    const [errors, setErrors] = useState({});

    // UI state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [touched, setTouched] = useState({});
    const [openJoiningDocumentsModal, setOpenJoiningDocumentsModal] = useState(false);
    const [selectedJoiningDocuments, setSelectedJoiningDocuments] = useState({});
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);

    // Data state
    const [entities, setEntities] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [locations, setLocations] = useState([]);
    const [managerOptions, setManagerOptions] = useState([]);
    const [managerLoading, setManagerLoading] = useState(false);
    const [managerSearch, setManagerSearch] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const skipManagerFetch = React.useRef(false);

    // Fetch Roles using RTK Query with error handling
    const {
        data: roles = [],
        isLoading: isRolesLoading,
        error: rolesError,
        refetch: refetchRoles
    } = useGetRolesQuery(undefined, {
        // Optionally refetch on mount or other conditions
        refetchOnMountOrArgChange: true
    });


    const roleLevelToNumber = (level) => {
        if (!level) return 99;
        const match = String(level).match(/(\d+)/);
        return match ? parseInt(match[0], 10) : 99;
    };

    // Since roles are not department-specific, we'll show all active roles
    const activeRoles = React.useMemo(() => {
        const filtered = roles.filter(role => role.status === 'Active' && role.code !== 'SUPER_ADMIN');

        // Role Level Filtering: Creator can't assign role with higher power (lower level number)
        if (user && user.role && user.role.hierarchyLevel) {
            const creatorLevel = roleLevelToNumber(user.role.hierarchyLevel);
            return filtered.filter(role => {
                const targetLevel = roleLevelToNumber(role.hierarchyLevel);
                // Level 1 > Level 2 > Level 3
                // Creator can only assign roles with level number >= their own level number
                return targetLevel >= creatorLevel;
            });
        }

        return filtered;
    }, [roles, user, user?.role?.hierarchyLevel]);

    // Fetch managers from backend when search or filters change
    useEffect(() => {
        if (skipManagerFetch.current) {
            skipManagerFetch.current = false;
            return;
        }

        const fetchManagers = async () => {
            setManagerLoading(true);
            try {
                const result = await getEmployees({
                    status: 'Active',
                    search: managerSearch,
                    entityId: formData.entityId,
                    departmentId: formData.departmentId,
                    jobLocation: formData.jobLocation
                });
                if (result.success) {
                    setManagerOptions(result.data || []);
                }
            } catch (error) {
                console.error('Error fetching managers:', error);
            } finally {
                setManagerLoading(false);
            }
        };

        const debounceTimer = setTimeout(() => {
            fetchManagers();
        }, 500);

        return () => clearTimeout(debounceTimer);
    }, [managerSearch, formData.entityId, formData.departmentId, formData.jobLocation]);

    // Fetch templates when entity changes
    useEffect(() => {
        const fetchTemplates = async () => {
            if (!formData.entityId) {
                setAvailableTemplates([]);
                return;
            }
            setIsLoadingTemplates(true);
            try {
                const response = await getOnboardingTemplatesByEntity(formData.entityId);
                if (response.success) {
                    setAvailableTemplates(response.data);
                    // Don't pre-select templates by default
                    setSelectedJoiningDocuments({});
                }
            } catch (error) {
                console.error('Error fetching templates:', error);
            } finally {
                setIsLoadingTemplates(false);
            }
        };

        fetchTemplates();
    }, [formData.entityId]);

    // Handle joining documents checkbox change
    const handleJoiningDocumentsChange = (e) => {
        const { checked } = e.target;
        setFormData(prev => ({
            ...prev,
            collectJoiningDocuments: checked
        }));

        if (checked) {
            setOpenJoiningDocumentsModal(true);
        }
    };

    // Handle document selection in modal
    const handleDocumentToggle = (templateId) => {
        setSelectedJoiningDocuments(prev => ({
            ...prev,
            [templateId]: !prev[templateId]
        }));
    };

    // Handle input change
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Handle blur event
    const handleBlur = (e) => {
        const { name, value } = e.target;
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));
        validateField(name, value);
    };

    // Validate a single field
    const validateField = (name, value) => {
        let error = '';

        switch (name) {
            case 'employeeId':
                if (!value.trim()) {
                    error = 'Employee ID is required';
                } else if (!/^[a-zA-Z0-9]+$/.test(value.trim())) {
                    error = 'Employee ID must be alphanumeric';
                }
                break;
            case 'firstName':
            case 'lastName':
                if (!value.trim()) error = 'This field is required';
                break;
            case 'email':
                if (!value) {
                    error = 'Email is required';
                } else if (!validateEmail(value)) {
                    error = 'Invalid email address';
                }
                break;
            case 'mobileNumber':
                if (!value) {
                    error = 'Mobile number is required';
                } else if (!validateMobile(value)) {
                    error = 'Mobile number must be 10 digits';
                }
                break;
            case 'jobLocation':
            case 'entityId':
            case 'departmentId':
            case 'roleId':
            case 'designationId':
            case 'educationLevel':
            case 'employmentType':
            case 'isFresher':
                if (!value) error = 'This field is required';
                break;
            default:
                break;
        }

        setErrors(prev => ({
            ...prev,
            [name]: error
        }));

        return !error;
    };

    // Validate entire form
    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        // Check required fields
        const requiredFields = [
            'employeeId', 'firstName', 'lastName', 'email', 'mobileNumber', 'jobLocation', 
            'entityId', 'departmentId', 'roleId', 'designationId', 
            'educationLevel', 'employmentType', 'isFresher'
        ];

        requiredFields.forEach(field => {
            if (formData[field] === undefined || formData[field] === null || formData[field] === '') {
                newErrors[field] = 'This field is required';
                isValid = false;
            }
        });

        // Validate employee ID format
        if (formData.employeeId && !/^[a-zA-Z0-9]+$/.test(formData.employeeId.trim())) {
            newErrors.employeeId = 'Employee ID must be alphanumeric';
            isValid = false;
        }

        // Validate email format
        if (formData.email && !validateEmail(formData.email)) {
            newErrors.email = 'Invalid email address';
            isValid = false;
        }

        // Validate mobile format
        if (formData.mobileNumber && !validateMobile(formData.mobileNumber)) {
            newErrors.mobileNumber = 'Mobile number must be 10 digits';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitted(true);

        if (!validateForm()) {
            return;
        }

        try {
            setIsSubmitting(true);
            setSubmitError('');

            if (formData.collectDocuments) {
                // Send invite link when collect documents is checked
                const employeeData = {
                    employeeId: formData.employeeId?.trim() || null,
                    firstName: formData.firstName.trim(),
                    lastName: formData.lastName.trim(),
                    email: formData.email.trim(),
                    phone: formData.mobileNumber.trim(),
                    entityId: formData.entityId,
                    departmentId: formData.departmentId,
                    roleId: formData.roleId,
                    designationId: formData.designationId,
                    educationLevel: formData.educationLevel,
                    workLocation: formData.jobLocation,
                    employmentType: formData.employmentType,
                    isFresher: formData.isFresher === 'yes',
                    hasExistingPF: formData.hasExistingPF,
                    collect12BB: formData.collect12BB,
                    collectESIC: formData.collectESIC,
                    managerId: formData.managerId || null,
                    collectDocumentsBeforeOffer: formData.collectDocuments,
                    initiateOnBoarding: true,
                    status: 'Onboarding',
                    sendInviteLink: true,
                    requiredTemplates: Object.keys(selectedJoiningDocuments).filter(id => selectedJoiningDocuments[id])
                };

                const response = await createEmployee(employeeData);

                if (response.success) {
                    showAlert({
                        title: 'Success',
                        message: `Invite link sent successfully! Employee ID: ${response.data.employeeId}`,
                        severity: 'success'
                    });

                    // Reset form
                    setFormData({
                        employeeId: '',
                        firstName: '',
                        lastName: '',
                        mobileNumber: '',
                        email: '',
                        jobLocation: '',
                        entityId: '',
                        departmentId: '',
                        roleId: '',
                        designationId: '',
                        educationLevel: '',
                        employmentType: 'Full-time',
                        isFresher: '',
                        hasExistingPF: false,
                        collect12BB: false,
                        collectESIC: false,
                        collectDocuments: true,
                        managerId: ''
                    });
                    setTouched({});
                    setSubmitted(false);
                    skipManagerFetch.current = true;
                    setManagerSearch('');
                    setManagerOptions([]);
                } else {
                    throw new Error(response.message || response.error || 'Failed to send invite link');
                }
            } else {
                // Route to add-employee page when collect documents is not checked
                const queryParams = new URLSearchParams({
                    employeeId: formData.employeeId.trim(),
                    firstName: formData.firstName.trim(),
                    lastName: formData.lastName.trim(),
                    email: formData.email.trim(),
                    mobileNumber: formData.mobileNumber.trim(),
                    entityId: formData.entityId,
                    departmentId: formData.departmentId,
                    roleId: formData.roleId,
                    designationId: formData.designationId,
                    educationLevel: formData.educationLevel,
                    employmentType: formData.employmentType,
                    isFresher: formData.isFresher,
                    hasExistingPF: formData.hasExistingPF,
                    collect12BB: formData.collect12BB,
                    collectESIC: formData.collectESIC,
                    managerId: formData.managerId || ''
                });

                navigate(`/my-team/add-employee?${queryParams.toString()}`);
            }

        } catch (error) {
            console.error('Submission error:', error);
            showError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Reset form
    const handleReset = () => {
        setFormData({
            employeeId: '',
            firstName: '',
            lastName: '',
            mobileNumber: '',
            email: '',
            jobLocation: '',
            entityId: '',
            departmentId: '',
            roleId: '',
            designationId: '',
            educationLevel: '',
            employmentType: 'Full-time',
            isFresher: '',
            hasExistingPF: false,
            collect12BB: false,
            collectESIC: false,
            collectDocuments: true,
            managerId: ''
        });
        setErrors({});
        setTouched({});
        setSubmitted(false);
        setSubmitError('');
        skipManagerFetch.current = true;
        setManagerSearch('');
        setManagerOptions([]);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const [entitiesRes, departmentsRes, designationsRes] = await Promise.all([
                    getEntities(),
                    getDepartments(),
                    getDesignations()
                ]);

                if (entitiesRes.success) setEntities(entitiesRes.data);
                if (departmentsRes.success) setDepartments(departmentsRes.data);
                if (designationsRes.success) setDesignations(designationsRes.data);
            } catch (error) {
                console.error('Error fetching data:', error);
                showError(error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        const fetchLocations = async () => {
            if (formData.entityId) {
                try {
                    const response = await getLocations({ entityId: formData.entityId });
                    if (response.success) {
                        setLocations(response.data);
                    } else {
                        setLocations([]);
                    }
                } catch (error) {
                    console.error('Error fetching locations:', error);
                    setLocations([]);
                }
            } else {
                setLocations([]);
            }
        };
        fetchLocations();
    }, [formData.entityId]);

    // Handle entity change to reset location and other dependent fields
    const handleEntityChange = (e) => {
        const newValue = e.target.value;
        setFormData(prev => {
            const newFormData = {
                ...prev,
                entityId: newValue,
                jobLocation: '' // Reset location when entity changes
            };
            return newFormData;
        });

        // Clear entity error if any
        if (errors.entityId) {
            setErrors(prev => ({
                ...prev,
                entityId: ''
            }));
        }

        // Clear location error if any
        if (errors.jobLocation) {
            setErrors(prev => ({
                ...prev,
                jobLocation: ''
            }));
        }
    };

    // Handle department change to reset role if needed
    const handleDepartmentChange = (e) => {
        const newValue = e.target.value;
        setFormData(prev => ({
            ...prev,
            departmentId: newValue,
            roleId: '' // Reset role when department changes
        }));

        // Clear department error if any
        if (errors.departmentId) {
            setErrors(prev => ({
                ...prev,
                departmentId: ''
            }));
        }

        // Clear role error if any
        if (errors.roleId) {
            setErrors(prev => ({
                ...prev,
                roleId: ''
            }));
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, py: 1 }}>
                {/* Breadcrumb Navigation */}
                <Breadcrumbs
                    separator={<NavigateNext sx={{ fontSize: "1rem", color: "#94a3b8" }} />}
                    sx={{
                        marginBottom: 3,
                        "& .MuiBreadcrumbs-li": {
                            fontSize: "0.875rem",
                        },
                    }}
                >
                    <Link
                        key="/dashboard"
                        underline="hover"
                        color="inherit"
                        sx={{
                            cursor: "pointer",
                            color: "#4F4937",
                            fontWeight: 400,
                            "&:hover": {
                                color: "#7522D1",
                            },
                        }}
                        onClick={() => navigate('/dashboard')}
                    >
                        Dashboard
                    </Link>
                    <Link
                        key="/admin/my-team/employee-directory"
                        underline="hover"
                        color="inherit"
                        sx={{
                            cursor: "pointer",
                            color: "#4F4937",
                            fontWeight: 400,
                            "&:hover": {
                                color: "#7522D1",
                            },
                        }}
                        onClick={() => navigate('/my-team/employee-directory')}
                    >
                        My Team
                    </Link>
                    <Typography
                        sx={{
                            cursor: "default",
                            color: "#7522D1",
                            fontWeight: 600,
                        }}
                    >
                        Initiate Onboarding
                    </Typography>
                </Breadcrumbs>

                {/* Page Header */}
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 800,
                                color: "#0F0A1E",
                                mb: 1,
                                letterSpacing: "-0.02em"
                            }}
                        >
                            Initiate Employee Onboarding
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: "#4F4937",
                                maxWidth: 600
                            }}
                        >
                            Fill in the employee details to start the onboarding process and create a new employee record in the system.
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<CloudUpload />}
                        onClick={() => setUploadModalOpen(true)}
                        sx={{
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 700,
                            border: '1px solid rgba(117, 34, 209, 0.2)',
                            color: '#7522D1',
                            backgroundColor: '#FFFFFF',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                border: '1px solid #7522D1',
                                backgroundColor: '#F5EEFC',
                                transform: 'translateY(-1px)'
                            }
                        }}
                    >
                        Bulk Upload Employees
                    </Button>
                </Box>

                {/* Main Content */}
                <Grid container spacing={3}>
                    {/* Full Width Form Section */}
                    <Grid size={{ xs: 12 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: '16px',
                                border: '1px solid rgba(117, 34, 209, 0.08)',
                                boxShadow: '0 8px 32px rgba(117, 34, 209, 0.03)',
                                backgroundColor: '#FFFFFF',
                                overflow: 'hidden'
                            }}
                        >
                            <Box
                                sx={{
                                    p: 3,
                                    background: 'linear-gradient(135deg, rgba(117, 34, 209, 0.03) 0%, rgba(156, 79, 242, 0.03) 100%)',
                                    borderBottom: '1px solid rgba(117, 34, 209, 0.08)'
                                }}
                            >
                                <Stack direction="row" alignItems="center" gap={2}>
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            borderRadius: '10px',
                                            backgroundColor: '#F5EEFC',
                                            color: '#7522D1',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Person fontSize="small" />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" fontWeight={750} color="#0F0A1E">
                                            Employee Onboarding Form
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Fill in the employee details to start the onboarding process
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            <Box sx={{ p: 4 }}>


                                <form onSubmit={handleSubmit}>
                                    <Grid container spacing={2.5}>
                                        {/* Employee ID */}
                                        <Grid size={{ xs: 12 }}>
                                            <TextField
                                                required
                                                fullWidth
                                                id="employeeId"
                                                name="employeeId"
                                                label="Employee ID"
                                                placeholder="Enter Employee ID"
                                                variant="outlined"
                                                value={formData.employeeId}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={(touched.employeeId || submitted) && Boolean(errors.employeeId)}
                                                helperText={(touched.employeeId || submitted) && errors.employeeId}
                                                size="small"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Badge color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={formInputStyle}
                                            />
                                        </Grid>

                                        {/* First Name */}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                fullWidth
                                                id="firstName"
                                                name="firstName"
                                                label="First Name"
                                                placeholder="Enter first name"
                                                variant="outlined"
                                                value={formData.firstName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={(touched.firstName || submitted) && Boolean(errors.firstName)}
                                                helperText={(touched.firstName || submitted) && errors.firstName}
                                                size="small"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Person color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={formInputStyle}
                                            />
                                        </Grid>

                                        {/* Last Name */}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                fullWidth
                                                id="lastName"
                                                name="lastName"
                                                label="Last Name"
                                                placeholder="Enter last name"
                                                variant="outlined"
                                                value={formData.lastName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={(touched.lastName || submitted) && Boolean(errors.lastName)}
                                                helperText={(touched.lastName || submitted) && errors.lastName}
                                                size="small"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Person color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={formInputStyle}
                                            />
                                        </Grid>

                                        {/* Mobile Number */}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                fullWidth
                                                id="mobileNumber"
                                                name="mobileNumber"
                                                label="Mobile Number"
                                                placeholder="Enter 10-digit mobile number"
                                                variant="outlined"
                                                value={formData.mobileNumber}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={(touched.mobileNumber || submitted) && Boolean(errors.mobileNumber)}
                                                helperText={(touched.mobileNumber || submitted) && errors.mobileNumber}
                                                size="small"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Phone color="action" />
                                                        </InputAdornment>
                                                    ),
                                                    inputProps: {
                                                        maxLength: 10
                                                    }
                                                }}
                                                sx={formInputStyle}
                                            />
                                        </Grid>

                                        {/* Email */}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                fullWidth
                                                id="email"
                                                name="email"
                                                label="Email Address"
                                                placeholder="Enter email address"
                                                variant="outlined"
                                                value={formData.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={(touched.email || submitted) && Boolean(errors.email)}
                                                helperText={(touched.email || submitted) && errors.email}
                                                size="small"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Email color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={formInputStyle}
                                            />
                                        </Grid>

                                        {/* Entity */}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                select
                                                fullWidth
                                                id="entityId"
                                                name="entityId"
                                                label="Entity"
                                                value={formData.entityId}
                                                onChange={handleEntityChange}
                                                onBlur={handleBlur}
                                                error={(touched.entityId || submitted) && Boolean(errors.entityId)}
                                                helperText={(touched.entityId || submitted) && errors.entityId}
                                                variant="outlined"
                                                size="small"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Business color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={formInputStyle}
                                            >
                                                <MenuItem value="" disabled>
                                                    Select Entity
                                                </MenuItem>
                                                {entities.map((entity) => (
                                                    <MenuItem key={entity.id} value={entity.id}>
                                                        {entity.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        {/* Job Location */}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Autocomplete
                                                id="jobLocation"
                                                options={locations}
                                                getOptionLabel={(option) => option.name || ''}
                                                value={locations.find(loc => loc.id === formData.jobLocation) || null}
                                                onChange={(event, newValue) => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        jobLocation: newValue ? newValue.id : ''
                                                    }));
                                                    if (errors.jobLocation) {
                                                        setErrors(prev => ({
                                                            ...prev,
                                                            jobLocation: ''
                                                        }));
                                                    }
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Job Location"
                                                        placeholder="Search and select job location..."
                                                        variant="outlined"
                                                        size="small"
                                                        error={(touched.jobLocation || submitted) && Boolean(errors.jobLocation)}
                                                        helperText={((touched.jobLocation || submitted) && errors.jobLocation) || (!formData.entityId ? "Please select an entity first" : "")}
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            startAdornment: (
                                                                <>
                                                                    <InputAdornment position="start">
                                                                        <Work color="action" />
                                                                    </InputAdornment>
                                                                    {params.InputProps.startAdornment}
                                                                </>
                                                            ),
                                                        }}
                                                        sx={formInputStyle}
                                                    />
                                                )}
                                                fullWidth
                                                disabled={!formData.entityId}
                                            />
                                        </Grid>

                                        {/* Department */}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                select
                                                fullWidth
                                                id="departmentId"
                                                name="departmentId"
                                                label="Department"
                                                value={formData.departmentId}
                                                onChange={handleDepartmentChange}
                                                onBlur={handleBlur}
                                                error={(touched.departmentId || submitted) && Boolean(errors.departmentId)}
                                                helperText={(touched.departmentId || submitted) && errors.departmentId}
                                                variant="outlined"
                                                size="small"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Group color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={formInputStyle}
                                            >
                                                <MenuItem value="" disabled>
                                                    Select Department
                                                </MenuItem>
                                                {departments.map((dept) => (
                                                    <MenuItem key={dept.id} value={dept.id}>
                                                        {dept.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        {/* Role */}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                select
                                                fullWidth
                                                id="roleId"
                                                name="roleId"
                                                label="System Role"
                                                value={formData.roleId}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={((touched.roleId || submitted) && Boolean(errors.roleId)) || Boolean(rolesError)}
                                                helperText={
                                                    ((touched.roleId || submitted) && errors.roleId) ||
                                                    (rolesError ? 'Failed to load roles. Please try again.' : '')
                                                }
                                                variant="outlined"
                                                size="small"
                                                disabled={isRolesLoading || Boolean(rolesError)}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Work color="action" />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: isRolesLoading ? (
                                                        <InputAdornment position="end">
                                                            <CircularProgress size={20} />
                                                        </InputAdornment>
                                                    ) : null,
                                                }}
                                                sx={formInputStyle}
                                            >
                                                <MenuItem value="" disabled>
                                                    {isRolesLoading
                                                        ? 'Loading roles...'
                                                        : rolesError
                                                            ? 'Error loading roles'
                                                            : activeRoles.length === 0
                                                                ? 'No roles available'
                                                                : 'Select System Role'}
                                                </MenuItem>
                                                {activeRoles.map((role) => (
                                                    <MenuItem key={role.id} value={role.id}>
                                                        {role.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            {rolesError && (
                                                <Button
                                                    size="small"
                                                    onClick={() => refetchRoles()}
                                                    startIcon={<RefreshIcon fontSize="small" />}
                                                    sx={{ mt: 1 }}
                                                >
                                                    Retry
                                                </Button>
                                            )}
                                        </Grid>

                                        {/* Designation */}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                select
                                                fullWidth
                                                id="designationId"
                                                name="designationId"
                                                label="Designation"
                                                value={formData.designationId}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={(touched.designationId || submitted) && Boolean(errors.designationId)}
                                                helperText={(touched.designationId || submitted) && errors.designationId}
                                                variant="outlined"
                                                size="small"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Badge color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={formInputStyle}
                                            >
                                                <MenuItem value="" disabled>
                                                    Select Designation
                                                </MenuItem>
                                                {designations.map((designation) => (
                                                    <MenuItem key={designation.id} value={designation.id}>
                                                        {designation.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        {/* Reporting Manager */}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Autocomplete
                                                id="managerId"
                                                options={managerOptions}
                                                loading={managerLoading}
                                                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                                                value={managerOptions.find(m => (m.id === formData.managerId || m._id === formData.managerId)) || null}
                                                onInputChange={(event, newInputValue, reason) => {
                                                    if (reason === 'input' || reason === 'clear') {
                                                        setManagerSearch(newInputValue);
                                                    }
                                                }}
                                                onChange={(event, newValue) => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        managerId: newValue ? (newValue.id || newValue._id) : ''
                                                    }));
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Reporting Manager"
                                                        variant="outlined"
                                                        size="small"
                                                        placeholder="Search manager..."
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            startAdornment: (
                                                                <>
                                                                    <InputAdornment position="start">
                                                                        <Person color="action" />
                                                                    </InputAdornment>
                                                                    {params.InputProps.startAdornment}
                                                                </>
                                                            ),
                                                            endAdornment: (
                                                                <React.Fragment>
                                                                    {managerLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                                    {params.InputProps.endAdornment}
                                                                </React.Fragment>
                                                            ),
                                                        }}
                                                        sx={formInputStyle}
                                                    />
                                                )}
                                                renderOption={(props, option) => (
                                                    <li {...props} key={option.id || option._id}>
                                                        <Box>
                                                            <Typography variant="body2">
                                                                 {option.firstName} {option.lastName}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {option.jobTitle || option.designation?.name || 'No Title'} • {option.department?.name || 'No Dept'}
                                                            </Typography>
                                                        </Box>
                                                    </li>
                                                )}
                                                fullWidth
                                            />
                                        </Grid>

                                        {/* Employment Type */}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                select
                                                fullWidth
                                                id="employmentType"
                                                name="employmentType"
                                                label="Employment Type"
                                                value={formData.employmentType}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={(touched.employmentType || submitted) && Boolean(errors.employmentType)}
                                                helperText={(touched.employmentType || submitted) && errors.employmentType}
                                                variant="outlined"
                                                size="small"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Work color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={formInputStyle}
                                            >
                                                <MenuItem value="" disabled>
                                                    Select Employment Type
                                                </MenuItem>
                                                {EMPLOYMENT_TYPES.map((type) => (
                                                    <MenuItem key={type.value} value={type.value}>
                                                        {type.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        {/* Education Level */}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                select
                                                fullWidth
                                                id="educationLevel"
                                                name="educationLevel"
                                                label="Education Level"
                                                value={formData.educationLevel}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={(touched.educationLevel || submitted) && Boolean(errors.educationLevel)}
                                                helperText={(touched.educationLevel || submitted) && errors.educationLevel}
                                                variant="outlined"
                                                size="small"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <School color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={formInputStyle}
                                            >
                                                <MenuItem value="" disabled>
                                                    Select Education Level
                                                </MenuItem>
                                                {EDUCATION_LEVELS.map((level) => (
                                                    <MenuItem key={level.value} value={level.value}>
                                                        {level.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        {/* Fresher */}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                select
                                                fullWidth
                                                id="isFresher"
                                                name="isFresher"
                                                label="Fresher"
                                                value={formData.isFresher}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={(touched.isFresher || submitted) && Boolean(errors.isFresher)}
                                                helperText={(touched.isFresher || submitted) && errors.isFresher}
                                                variant="outlined"
                                                size="small"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <School color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={formInputStyle}
                                            >
                                                <MenuItem value="" disabled>
                                                    Select
                                                </MenuItem>
                                                <MenuItem value="yes">Yes</MenuItem>
                                                <MenuItem value="no">No</MenuItem>
                                            </TextField>
                                        </Grid>

                                        {/* Collect Form 12BB */}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: '10px', height: '100%' }}>
                                                <FormGroup>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch 
                                                                checked={formData.collect12BB} 
                                                                onChange={handleChange} 
                                                                name="collect12BB" 
                                                                color="primary" 
                                                            />
                                                        }
                                                        label={<Typography variant="subtitle2" fontWeight="bold">Form 12BB</Typography>}
                                                    />
                                                </FormGroup>
                                                {formData.collect12BB && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                                                        - Employee will be required to complete and submit Form 12BB.
                                                    </Typography>
                                                )}
                                            </Paper>
                                        </Grid>

                                        {/* Collect ESIC Form */}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: '10px', height: '100%' }}>
                                                <FormGroup>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch 
                                                                checked={formData.collectESIC} 
                                                                onChange={handleChange} 
                                                                name="collectESIC" 
                                                                color="primary" 
                                                            />
                                                        }
                                                        label={<Typography variant="subtitle2" fontWeight="bold">ESIC Details</Typography>}
                                                    />
                                                </FormGroup>
                                                {formData.collectESIC && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                                                        - Employee will be required to complete ESIC Form I and provide dependent details.
                                                    </Typography>
                                                )}
                                            </Paper>
                                        </Grid>

                                        {/* Has Existing PF */}
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: '10px', height: '100%' }}>
                                                <FormGroup>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch 
                                                                checked={formData.hasExistingPF} 
                                                                onChange={handleChange} 
                                                                name="hasExistingPF" 
                                                                color="primary" 
                                                            />
                                                        }
                                                        label={<Typography variant="subtitle2" fontWeight="bold">PF Declaration</Typography>}
                                                    />
                                                </FormGroup>
                                                {formData.hasExistingPF && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                                                        - Employee will be required to complete and submit the PF Declaration Form.
                                                    </Typography>
                                                )}
                                            </Paper>

                                            {formData.collectDocuments && (
                                                <Box sx={{ mt: 2, mb: 1 }}>
                                                    <Autocomplete
                                                        multiple
                                                        id="joining-documents-select"
                                                        options={availableTemplates}
                                                        disableCloseOnSelect
                                                        getOptionLabel={(option) => option.label || option.templateName}
                                                        value={availableTemplates.filter(t => selectedJoiningDocuments[t.id])}
                                                        onChange={(event, newValue) => {
                                                            const newSelection = {};
                                                            newValue.forEach(tpl => {
                                                                newSelection[tpl.id] = true;
                                                            });
                                                            setSelectedJoiningDocuments(newSelection);
                                                        }}
                                                        renderOption={(props, option, { selected }) => (
                                                            <li {...props}>
                                                                <Checkbox
                                                                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                                                                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                                    style={{ marginRight: 8 }}
                                                                    checked={selected}
                                                                    sx={{
                                                                        color: '#D1D5DB',
                                                                        '&.Mui-checked': { color: '#7522D1' }
                                                                    }}
                                                                />
                                                                <Box>
                                                                    <Typography variant="body2">
                                                                        {option.label || option.templateName}
                                                                    </Typography>
                                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                                        {option.entityId ? 'Entity Specific' : 'Global Template'}
                                                                    </Typography>
                                                                </Box>
                                                            </li>
                                                        )}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Select Required Joining Documents"
                                                                placeholder="Search templates..."
                                                                size="small"
                                                                variant="outlined"
                                                                helperText={availableTemplates.length === 0 ? "No templates found for this entity" : ""}
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    startAdornment: (
                                                                        <>
                                                                            <InputAdornment position="start">
                                                                                <DescriptionIcon color="action" fontSize="small" />
                                                                            </InputAdornment>
                                                                            {params.InputProps.startAdornment}
                                                                        </>
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                        renderTags={(value, getTagProps) =>
                                                            value.map((option, index) => (
                                                                <Chip
                                                                    label={option.label || option.templateName}
                                                                    size="small"
                                                                    {...getTagProps({ index })}
                                                                    sx={{
                                                                        bgcolor: '#F5EEFC',
                                                                        color: '#7522D1',
                                                                        fontWeight: 600,
                                                                        border: '1px solid rgba(117, 34, 209, 0.15)',
                                                                        borderRadius: '6px'
                                                                    }}
                                                                />
                                                            ))
                                                        }
                                                        sx={formInputStyle}
                                                    />
                                                </Box>
                                            )}

                                        </Grid>

                                        {/* Collect Documents Checkbox */}
                                        <Grid size={{ xs: 12 }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        id="collectDocuments"
                                                        name="collectDocuments"
                                                        checked={formData.collectDocuments}
                                                        onChange={handleChange}
                                                        sx={{
                                                            color: '#D1D5DB',
                                                            '&.Mui-checked': { color: '#7522D1' }
                                                        }}
                                                    />
                                                }
                                                label="Allow candidate to upload documents"
                                                sx={{
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.875rem',
                                                        color: theme.palette.text.primary
                                                    }
                                                }}
                                            />
                                        </Grid>

                                        {/* Action Buttons */}
                                        <Grid size={{ xs: 12 }}>
                                            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(117, 34, 209, 0.08)' }}>
                                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
                                                    <Button
                                                        type="button"
                                                        variant="outlined"
                                                        onClick={handleReset}
                                                        disabled={isSubmitting}
                                                        size="large"
                                                        sx={{
                                                            borderRadius: '12px',
                                                            py: 1.5,
                                                            px: 4,
                                                            textTransform: 'none',
                                                            fontWeight: 700,
                                                            border: '1px solid rgba(117, 34, 209, 0.2)',
                                                            color: '#7522D1',
                                                            backgroundColor: '#FFFFFF',
                                                            transition: 'all 0.2s ease-in-out',
                                                            '&:hover': {
                                                                border: '1px solid #7522D1',
                                                                color: '#7522D1',
                                                                backgroundColor: '#F5EEFC',
                                                                transform: 'translateY(-1px)'
                                                            }
                                                        }}
                                                    >
                                                        Reset Form
                                                    </Button>

                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        disabled={isSubmitting}
                                                        size="large"
                                                        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                                                        sx={{
                                                            borderRadius: '12px',
                                                            py: 1.5,
                                                            px: 4,
                                                            textTransform: 'none',
                                                            fontWeight: 750,
                                                            backgroundColor: '#7522D1',
                                                            color: '#FFFFFF',
                                                            boxShadow: '0 4px 14px rgba(117, 34, 209, 0.2)',
                                                            transition: 'all 0.2s ease-in-out',
                                                            '&:hover': {
                                                                backgroundColor: '#6A1DB0',
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: '0 6px 20px rgba(117, 34, 209, 0.3)',
                                                            }
                                                        }}
                                                    >
                                                        {isSubmitting ? 'Creating Employee...' : 'Initiate Employee Onboarding'}
                                                    </Button>
                                                </Stack>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </form>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>

            {/* Add ErrorModal component */}
            <ErrorModal />

            {/* Alert Dialog */}
            <AlertDialog
                open={alertDialog.open}
                onClose={closeAlert}
                title={alertDialog.title}
                message={alertDialog.message}
                severity={alertDialog.severity}
                confirmText={alertDialog.confirmText}
                cancelText={alertDialog.cancelText}
                showCancel={alertDialog.showCancel}
                onConfirm={alertDialog.onConfirm}
            />


            {/* Bulk Upload Modal */}
            <BulkUploadModal
                open={uploadModalOpen}
                onClose={() => setUploadModalOpen(false)}
                onUpload={bulkUploadEmployees}
                title="Bulk Upload Employees"
                moduleType="employee"
                optionsForTemplate={{
                    entities: entities.map(e => e.name),
                    departments: departments.map(d => d.name),
                    roles: activeRoles.map(r => r.name),
                    designations: designations.map(d => d.name)
                }}
                onSuccess={() => {
                    showAlert({
                        title: 'Success',
                        message: 'Employees uploaded and initiated successfully!',
                        severity: 'success'
                    });
                }}
            />
        </>
    );
};

export default InitiateOnboarding;
